import { getFrameRatio } from './chunk-WILRAE77.js';

// src/utils.ts
function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}
function aLerp(a, b, t) {
  return a * (1 - t) + b * t;
}
function iLerp(t, s, i) {
  return clamp((i - t) / (s - t), 0, 1);
}
function damp(current, target, lambda = 0.12) {
  const ratio = Math.max(getFrameRatio(), 0);
  const strength = Math.min(Math.max(lambda, 0), 0.999999);
  if (ratio <= 0 || strength <= 0) return current;
  const weight = 1 - Math.exp(Math.log(1 - strength) * ratio);
  return aLerp(current, target, weight);
}
function round(val, p = 3) {
  const m = Math.pow(10, p | 0);
  return Math.round((val + Number.EPSILON) * m) / m;
}
function unequal(a, b, precision = 3) {
  return round(Math.abs(a - b), precision) !== 0;
}
function translate3d(el, x, y, unit = "%") {
  if (!el?.style) return;
  const suffix = unit ?? "%";
  el.style.transform = `translate3d(${x}${suffix},${y}${suffix},0)`;
}
function cubicBezier(x1, y1, x2, y2) {
  function A(aA1, aA2) {
    return 1 - 3 * aA2 + 3 * aA1;
  }
  function B(aA1, aA2) {
    return 3 * aA2 - 6 * aA1;
  }
  function C(aA1) {
    return 3 * aA1;
  }
  function calcBezier(aT, aA1, aA2) {
    return ((A(aA1, aA2) * aT + B(aA1, aA2)) * aT + C(aA1)) * aT;
  }
  function getSlope(aT, aA1, aA2) {
    return 3 * A(aA1, aA2) * aT * aT + 2 * B(aA1, aA2) * aT + C(aA1);
  }
  function getTForX(aX) {
    let aGuessT = aX;
    for (let i = 0; i < 4; ++i) {
      const currentSlope = getSlope(aGuessT, x1, x2);
      if (currentSlope === 0) return aGuessT;
      const currentX = calcBezier(aGuessT, x1, x2) - aX;
      aGuessT -= currentX / currentSlope;
    }
    return aGuessT;
  }
  return function bezier(t) {
    if (x1 === y1 && x2 === y2) return t;
    const x = clamp(t, 0, 1);
    const paramT = getTForX(x);
    return clamp(calcBezier(paramT, y1, y2), 0, 1);
  };
}
var Ease = {
  linear: (t) => t,
  o6: cubicBezier(0.16, 1, 0.3, 1),
  io6: cubicBezier(0.87, 0, 0.13, 1),
  oC: cubicBezier(0, 0.55, 0.45, 1),
  oQ: cubicBezier(0.22, 1, 0.36, 1),
  o2: cubicBezier(0.5, 1, 0.89, 1),
  i1: cubicBezier(0.55, 0.055, 0.675, 0.19),
  i3: cubicBezier(0.55, 0.085, 0.68, 0.53),
  o1: cubicBezier(0.215, 0.61, 0.355, 1),
  o3: cubicBezier(0.25, 0.46, 0.45, 0.94),
  ioQ: cubicBezier(0.83, 0, 0.17, 1),
  ioW: cubicBezier(0.76, 0, 0.24, 1),
  oW: cubicBezier(0, 0.76, 0.24, 1)
};
var linearEase = (t) => t;
function ease4(arr) {
  if (!Array.isArray(arr) || arr.length !== 4) return linearEase;
  const [a, b, c, d] = arr;
  if (a === void 0 || b === void 0 || c === void 0 || d === void 0)
    return linearEase;
  return cubicBezier(a, b, c, d);
}
function bindMethod(ctx, names) {
  if (!ctx || !Array.isArray(names)) return;
  for (const n of names) {
    const method = ctx[n];
    if (typeof method === "function") {
      ctx[n] = method.bind(ctx);
    }
  }
}
var Random = {
  range(min, max, p = 0) {
    const precision = Math.pow(10, p);
    return Math.round((Math.random() * (max - min) + min) * precision) / precision;
  },
  uniq(n) {
    const a = Array.from({ length: n }, (_, i) => i);
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = a[i];
      const swap = a[j];
      if (temp !== void 0 && swap !== void 0) {
        a[i] = swap;
        a[j] = temp;
      }
    }
    return a;
  }
};
function bounds(el) {
  return el?.getBoundingClientRect?.() ?? { left: 0, top: 0, width: 0, height: 0 };
}
var Sniff = {
  get uA() {
    return navigator.userAgent.toLowerCase();
  },
  get iPadIOS13() {
    return navigator.platform === "MacIntel" && (navigator.maxTouchPoints ?? 0) > 1;
  },
  get isMobile() {
    return /mobi|android|tablet|ipad|iphone/.test(this.uA) || this.iPadIOS13;
  },
  get isFirefox() {
    return this.uA.indexOf("firefox") > -1;
  }
};
function preventDefault(e) {
  if (e && "cancelable" in e && e.cancelable) e.preventDefault();
}
function stop(inst) {
  try {
    if (inst && typeof inst.stop === "function") inst.stop();
  } catch {
  }
}
function has(o, k) {
  return o != null && Object.prototype.hasOwnProperty.call(o, k);
}
function def(v) {
  return typeof v !== "undefined" && v !== null;
}
function queryAll(root, selector) {
  if (!root) return [];
  try {
    return Array.from(
      root.querySelectorAll(selector)
    );
  } catch {
    return [];
  }
}
function toPx(rem) {
  const cs = getComputedStyle(document.documentElement);
  const fontSize = parseFloat(cs.fontSize || "0") || 0;
  return rem * fontSize;
}
function setTheme(theme) {
  const html = document.documentElement;
  html.setAttribute("theme", theme);
}
var utils = {
  clamp,
  lerp,
  damp,
  cubicBezier,
  Ease,
  ease4,
  bindMethod,
  unequal,
  translate3d,
  Random,
  bounds,
  Sniff,
  preventDefault,
  stop,
  has,
  def,
  queryAll,
  aLerp,
  iLerp,
  toPx
};
var utils_default = utils;

export { Ease, Random, Sniff, aLerp, bindMethod, bounds, clamp, cubicBezier, damp, def, ease4, has, iLerp, lerp, preventDefault, queryAll, round, setTheme, stop, toPx, translate3d, unequal, utils_default };
//# sourceMappingURL=chunk-QF7HVQZZ.js.map
//# sourceMappingURL=chunk-QF7HVQZZ.js.map